import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/6716cae1b0523b72f10adf96b3438159.png" 
              alt="Aeris Innovation Logo" 
              className="h-28 w-28 object-contain"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('how-we-work')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              How We Work
            </button>
            <button 
              onClick={() => scrollToSection('industries')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              Industries
            </button>
            <button 
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-6 py-2 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/f0481d058366b74e2593e05b7e49db30.jpg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/40 via-gray-900/20 to-gray-900/40"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-4xl">
            <div className="mb-8 inline-block px-4 py-2 border border-white/30 rounded-full">
              <p className="text-white/80 text-sm font-medium tracking-wide">Where guidance meets intelligent systems</p>
            </div>
            
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
              Smart systems.<br />
              Streamlined workflows.<br />
              <span className="text-teal-400">Stronger bottom line.</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-12 max-w-2xl leading-relaxed">
              Aeris Innovation helps service-driven businesses replace operational chaos with clarity—through expert advisory and precision-built systems designed for measurable results.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:shadow-lg hover:shadow-teal-600/50 whitespace-nowrap cursor-pointer"
              >
                Schedule a Strategy Session
              </button>
              <button 
                onClick={() => scrollToSection('how-we-work')}
                className="px-8 py-4 bg-transparent border-2 border-white text-white text-base font-semibold rounded-full hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
              >
                See How We Work
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="bg-gradient-to-r from-teal-50 to-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-lg text-gray-800 font-medium">
            We operate as a strategic partner—not a software vendor—guiding businesses toward structured workflows that improve performance, confidence, and profitability.
          </p>
        </div>
      </section>

      {/* Problem Solution Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
            When operations lack structure, growth becomes fragile.
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <p className="text-xl text-gray-700 leading-relaxed">
                As businesses scale, complexity compounds. Tools multiply. Processes fracture. Decision-making slows.
              </p>
            </div>
            <div>
              <p className="text-xl text-gray-700 leading-relaxed">
                What once worked becomes difficult to manage—and costly to maintain.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <p className="text-2xl font-semibold text-gray-900 mb-4">
              Aeris Innovation exists to bring order to that complexity.
            </p>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              We combine high-level advisory with intelligent system architecture to create workflows that are intentional, measurable, and built to scale.
            </p>
          </div>
        </div>
      </section>

      {/* Core Value Pillars */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-white to-teal-50/30 rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all duration-300 group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-compass-3-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Guidance</h3>
              <div className="w-10 h-0.5 bg-teal-600 mb-6"></div>
              <p className="text-base text-gray-700 leading-relaxed">
                Strategic advisory rooted in experience—not assumptions. We help you see clearly, prioritize effectively, and move forward with confidence.
              </p>
            </div>

            <div className="bg-gradient-to-b from-white to-teal-50/30 rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all duration-300 group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-stack-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Structure</h3>
              <div className="w-10 h-0.5 bg-teal-600 mb-6"></div>
              <p className="text-base text-gray-700 leading-relaxed">
                Operational systems designed with precision. Every workflow is intentional, documented, and aligned to your business goals.
              </p>
            </div>

            <div className="bg-gradient-to-b from-white to-teal-50/30 rounded-2xl p-10 shadow-sm hover:shadow-xl transition-all duration-300 group">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Results</h3>
              <div className="w-10 h-0.5 bg-teal-600 mb-6"></div>
              <p className="text-base text-gray-700 leading-relaxed">
                Measurable improvements across efficiency, performance, and margins. Clarity isn't the goal—outcomes are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
            Advisory-led systems built for real business impact.
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div 
              className={`bg-white rounded-3xl shadow-lg overflow-hidden transition-all duration-300 ${
                activeService === 1 ? 'transform scale-105 shadow-2xl' : ''
              }`}
              onMouseEnter={() => setActiveService(1)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-white/80 text-sm font-bold">01</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-3xl text-white"></i>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-white">Operational Strategy & Advisory</h3>
              </div>
              <div className="p-8">
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  We assess your current operations, identify friction points, and design a clear roadmap for improvement—before technology is ever applied.
                </p>
                <p className="text-sm font-semibold text-gray-900 mb-3">Outcomes include:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Clear operational priorities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Defined workflows and ownership</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Reduced complexity across teams</span>
                  </li>
                </ul>
              </div>
            </div>

            <div 
              className={`bg-white rounded-3xl shadow-lg overflow-hidden transition-all duration-300 ${
                activeService === 2 ? 'transform scale-105 shadow-2xl' : ''
              }`}
              onMouseEnter={() => setActiveService(2)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-white/80 text-sm font-bold">02</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-flow-chart text-3xl text-white"></i>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-white">Workflow Architecture & Automation</h3>
              </div>
              <div className="p-8">
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  We design and implement intelligent workflows that reduce manual effort, improve accuracy, and create consistency across your business.
                </p>
                <p className="text-sm font-semibold text-gray-900 mb-3">Use cases include:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Client onboarding & fulfillment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Internal operations & task management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Lead handling & follow-up systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Reporting & performance tracking</span>
                  </li>
                </ul>
              </div>
            </div>

            <div 
              className={`bg-white rounded-3xl shadow-lg overflow-hidden transition-all duration-300 ${
                activeService === 3 ? 'transform scale-105 shadow-2xl' : ''
              }`}
              onMouseEnter={() => setActiveService(3)}
              onMouseLeave={() => setActiveService(null)}
            >
              <div className="bg-gradient-to-r from-teal-600 to-teal-500 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-white/80 text-sm font-bold">03</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-rocket-line text-3xl text-white"></i>
                  </div>
                </div>
                <h3 className="text-3xl font-bold text-white">Systems Optimization & Scaling</h3>
              </div>
              <div className="p-8">
                <p className="text-base text-gray-700 mb-6 leading-relaxed">
                  As your business grows, systems must evolve. We refine, optimize, and future-proof your workflows to support scale—without adding unnecessary complexity.
                </p>
                <p className="text-sm font-semibold text-gray-900 mb-3">Focus areas:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">System consolidation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Process refinement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Performance optimization</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600 mt-0.5"></i>
                    <span className="text-base text-gray-700">Long-term scalability</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section id="how-we-work" className="py-24 bg-gradient-to-r from-teal-50 via-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
            A structured approach—by design.
          </h2>
          
          <div className="grid md:grid-cols-4 gap-6 relative">
            {/* Connection Lines */}
            <div className="hidden md:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
            
            <div className="relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="absolute -top-4 left-8 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                01
              </div>
              <div className="mt-8">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-search-eye-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Strategic Assessment</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  We analyze your operations, workflows, and objectives to identify what matters most.
                </p>
              </div>
            </div>

            <div className="relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="absolute -top-4 left-8 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                02
              </div>
              <div className="mt-8">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-draft-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">System Design</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  We architect workflows that align strategy with execution—intentionally and precisely.
                </p>
              </div>
            </div>

            <div className="relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="absolute -top-4 left-8 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                03
              </div>
              <div className="mt-8">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-tools-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Implementation</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  Systems are built, documented, and integrated into your operations with minimal disruption.
                </p>
              </div>
            </div>

            <div className="relative bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all">
              <div className="absolute -top-4 left-8 w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                04
              </div>
              <div className="mt-8">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-bar-chart-grouped-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Optimization & Growth</h3>
                <p className="text-base text-gray-700 leading-relaxed">
                  We refine performance, track results, and support continued improvement over time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Representative Engagements Section */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-6">
            Representative Engagements
          </h2>
          <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto">
            Real outcomes from structured thinking and intelligent systems.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-10 shadow-md hover:shadow-xl transition-all duration-300 border-t-2 border-amber-500">
              <div className="w-12 h-0.5 bg-amber-500 mb-6"></div>
              <p className="text-sm font-semibold text-amber-600 tracking-wide mb-3">PROFESSIONAL SERVICES FIRM</p>
              <h3 className="text-3xl font-serif text-gray-900 mb-6 leading-tight">
                Bringing Structure to a Growing Advisory Practice
              </h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                As the firm scaled, internal workflows became inconsistent and leadership time was increasingly spent managing execution.
              </p>
              <div className="mb-6">
                <p className="text-sm font-bold text-gray-900 mb-3">Our work:</p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Strategic advisory and standardized workflow design to align teams and clarify ownership.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-3">Impact:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Reduced manual coordination</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Improved delivery consistency</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Restored leadership focus on strategy</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-10 shadow-md hover:shadow-xl transition-all duration-300 border-t-2 border-amber-500">
              <div className="w-12 h-0.5 bg-amber-500 mb-6"></div>
              <p className="text-sm font-semibold text-amber-600 tracking-wide mb-3">REAL ESTATE SERVICES COMPANY</p>
              <h3 className="text-3xl font-serif text-gray-900 mb-6 leading-tight">
                From Fragmented Operations to Scalable Performance
              </h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                Disconnected tools and manual follow-ups made growth difficult to sustain during peak transaction volume.
              </p>
              <div className="mb-6">
                <p className="text-sm font-bold text-gray-900 mb-3">Our work:</p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Unified workflow architecture and structured follow-up systems.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-3">Impact:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Stronger follow-through</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Improved transaction visibility</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">More predictable execution</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-10 shadow-md hover:shadow-xl transition-all duration-300 border-t-2 border-amber-500">
              <div className="w-12 h-0.5 bg-amber-500 mb-6"></div>
              <p className="text-sm font-semibold text-amber-600 tracking-wide mb-3">FINANCIAL & ADVISORY FIRM</p>
              <h3 className="text-3xl font-serif text-gray-900 mb-6 leading-tight">
                Strengthening Operational Confidence in a High-Trust Environment
              </h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                Undocumented processes and inconsistent execution increased operational risk.
              </p>
              <div className="mb-6">
                <p className="text-sm font-bold text-gray-900 mb-3">Our work:</p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Process standardization, role clarity, and structured review workflows.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-3">Impact:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Reduced process variance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Improved compliance readiness</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Greater confidence in daily operations</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white rounded-2xl p-10 shadow-md hover:shadow-xl transition-all duration-300 border-t-2 border-amber-500">
              <div className="w-12 h-0.5 bg-amber-500 mb-6"></div>
              <p className="text-sm font-semibold text-amber-600 tracking-wide mb-3">MULTI-LOCATION SERVICE BUSINESS</p>
              <h3 className="text-3xl font-serif text-gray-900 mb-6 leading-tight">
                Creating Consistency Across Distributed Teams
              </h3>
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                Independent team workflows led to inconsistent client experience and reporting gaps.
              </p>
              <div className="mb-6">
                <p className="text-sm font-bold text-gray-900 mb-3">Our work:</p>
                <p className="text-base text-gray-700 leading-relaxed">
                  Modular system architecture and centralized operational standards.
                </p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-3">Impact:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Consistent execution across locations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Improved reporting accuracy</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-base text-gray-700">Scalable operational foundation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={() => scrollToSection('contact')}
              className="px-10 py-4 bg-teal-600 text-white text-base font-semibold rounded-full hover:bg-teal-700 transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              Schedule a Strategy Session
            </button>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-gray-900 text-center mb-8">
            Built for service-driven organizations.
          </h2>
          <p className="text-xl text-gray-700 text-center mb-16 max-w-3xl mx-auto">
            Aeris Innovation works best with businesses where precision, trust, and performance matter.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl p-8 hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-briefcase-line text-4xl text-teal-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Professional Services</h3>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl p-8 hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-4xl text-orange-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Real Estate & Property Services</h3>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-8 hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-4xl text-emerald-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Financial & Advisory Firms</h3>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-rose-100/50 rounded-2xl p-8 hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-lg">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-heart-pulse-line text-4xl text-rose-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Healthcare & Regulated Services</h3>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-2xl p-8 hover:scale-105 transition-transform duration-300 shadow-sm hover:shadow-lg w-full md:w-1/2">
              <div className="w-14 h-14 flex items-center justify-center mb-4">
                <i className="ri-settings-3-line text-4xl text-amber-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Operationally Complex Service Businesses</h3>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">
                An advisor first. A systems architect always.
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Aeris Innovation was built on a simple belief: clarity creates confidence—and confidence drives growth.
                </p>
                <div className="space-y-3 my-8">
                  <p className="flex items-start gap-3">
                    <span className="text-teal-600 font-bold">—</span>
                    <span>We don't sell tools.</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-teal-600 font-bold">—</span>
                    <span>We don't push trends.</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-teal-600 font-bold">—</span>
                    <span>We don't overwhelm with complexity.</span>
                  </p>
                </div>
                <p>
                  Instead, we guide businesses through structured thinking, refined workflows, and intelligent systems that support long-term performance.
                </p>
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="bg-white rounded-2xl p-10 shadow-lg border-l-4 border-teal-600">
                <p className="text-3xl font-serif italic text-teal-700 leading-relaxed">
                  "Clarity creates confidence—and confidence drives growth."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact" className="py-32 bg-gradient-to-br from-gray-900 via-teal-900 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20with%20interconnected%20nodes%20and%20flowing%20lines%20representing%20business%20systems%20and%20workflows%20in%20dark%20background%20with%20subtle%20teal%20and%20white%20accents%20minimal%20modern%20technology%20aesthetic&width=1920&height=800&seq=cta-bg-002&orientation=landscape)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-6xl font-bold text-white mb-8">
            Clarity starts with the right guidance.
          </h2>
          <p className="text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-relaxed">
            If your operations feel complex, fragmented, or difficult to scale, the solution isn't more software—it's better structure.
          </p>
          <p className="text-lg text-white/80 mb-10">
            Let's build systems that work with your business, not against it.
          </p>
          <button 
            onClick={() => {
              const email = 'contact@aerisinnovation.com';
              const subject = 'Strategy Session Request';
              const body = 'Hi, I would like to schedule a strategy session to discuss how Aeris Innovation can help improve our operations.';
              window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            }}
            className="px-12 py-5 bg-white text-gray-900 text-lg font-bold rounded-full hover:bg-gray-100 transition-all shadow-2xl hover:shadow-white/20 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Book a Strategy Session
          </button>
          <p className="text-sm text-white/60 mt-6">No commitment required • 30-minute session</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/6716cae1b0523b72f10adf96b3438159.png" 
                  alt="Aeris Innovation" 
                  className="h-28 w-28 object-contain"
                />
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                Smart systems. Streamlined workflows. Stronger bottom line.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-base">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    Operational Strategy
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    Workflow Automation
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    Systems Optimization
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-base">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('how-we-work')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    How We Work
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('industries')} className="text-white/70 hover:text-teal-400 transition-colors cursor-pointer">
                    Industries
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-base">Contact</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <a href="mailto:contact@aerisinnovation.com" className="hover:text-teal-400 transition-colors">
                    contact@aerisinnovation.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <span>Schedule a call</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/50 text-sm">
                © 2025 Aeris Innovation. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-white/50 hover:text-teal-400 text-sm transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-white/50 hover:text-teal-400 text-sm transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
